import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { Link } from "react-router-dom";
import FloatingHero3D from "../FloatingHero3D";
import { CALENDLY_URL } from "../../data/content";

export const Hero = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen pt-28 md:pt-32 pb-16 overflow-hidden"
    >
      {/* Background gradient + grid */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#0a0a0a] to-[#050505]" />
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "80px 80px",
            maskImage:
              "radial-gradient(ellipse at 50% 50%, black 30%, transparent 80%)",
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-12 items-center">
        {/* Left: Text */}
        <div className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="inline-flex items-center gap-2 border border-white/15 px-3 py-1.5 text-[10px] md:text-xs uppercase tracking-[0.3em] text-white/70 mb-6"
            data-testid="hero-badge"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
            3–5 Days Delivery
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="font-display text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter uppercase leading-[0.95]"
            data-testid="hero-heading"
          >
            We forge <span className="text-violet-glow">brands</span>
            <br />
            in obsidian.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="mt-6 text-base md:text-lg text-white/60 max-w-xl leading-relaxed"
            data-testid="hero-subheading"
          >
            A brand & design agency for ecommerce brands. We build websites,
            plans, do consultations and craft brand identity systems that cut
            through.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-primary-cta"
              className="shine-box group inline-flex items-center gap-3 btn-violet px-6 py-3.5 text-xs md:text-sm uppercase tracking-[0.25em] font-semibold"
            >
              Book a Zoom Call
              <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
            <Link
              to="/portfolio"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center gap-2 border border-white/20 text-white px-6 py-3.5 text-xs md:text-sm uppercase tracking-[0.25em] font-semibold hover:bg-white/10 transition-colors"
            >
              View Portfolio
            </Link>
          </motion.div>

          {/* Mini stats */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.7 }}
            className="mt-12 md:mt-16 grid grid-cols-3 gap-4 max-w-md border-t border-white/10 pt-6"
          >
            {[
              { n: "150+", l: "Projects" },
              { n: "60+", l: "Brands" },
              { n: "5yrs", l: "Experience" },
            ].map((s) => (
              <div key={s.l}>
                <div className="font-display text-2xl md:text-3xl font-black">{s.n}</div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mt-1">
                  {s.l}
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right: Floating 3D scene */}
        <div className="relative h-[460px] md:h-[580px] lg:h-[640px]">
          <FloatingHero3D />
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/40"
      >
        <div className="text-[10px] uppercase tracking-[0.4em]">Scroll</div>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.6, repeat: Infinity }}
        >
          <ArrowDown size={14} />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
